import React from 'react';
import { Link } from 'react-router-dom'; // Import Link for navigation
import SEOHelmet from './utils/SEOHelmet';
import './LandingPage.css'; // We'll create this for styling

function LandingPage() {
    return (
        <>
            <SEOHelmet 
                title="Fish MD - Instant Saltwater Fish Disease Diagnosis | AI-Powered"
                description="Get instant, accurate diagnosis for sick saltwater fish. Identify Ich, Marine Velvet, Brooklynella & 20+ diseases with AI visual analysis. Free & trusted by 1000+ aquarists."
                keywords="saltwater fish diseases, marine fish illness, ich treatment, marine velvet diagnosis, fish disease identification, aquarium fish health, white spots on fish, fish rubbing on rocks, fish breathing rapidly"
                canonicalUrl="/"
            />
            <div className="landing-page">
                {/* The background will be handled by App.css or a shared style if needed */}
                <header className="landing-header">
                    <h1>Welcome to Fish MD</h1>
                    <p>Instant AI-Powered Diagnosis for Sick Saltwater Aquarium Fish</p>
                </header>

                <section id="usp-highlights" className="landing-section">
                    <h2>Why Choose Fish MD?</h2>
                    <div className="usp-grid">
                        <div className="usp-item">
                            <span className="usp-icon">⚡</span>
                            <h3>Instant Diagnosis</h3>
                            <p>Get results in seconds, not hours. When every minute counts for your fish's health.</p>
                        </div>
                        <div className="usp-item">
                            <span className="usp-icon">🎯</span>
                            <h3>High Accuracy</h3>
                            <p>Refined on thousands of verified cases. More accurate than forum guesswork.</p>
                        </div>
                        <div className="usp-item">
                            <span className="usp-icon">💬</span>
                            <h3>Conversational Follow-up</h3>
                            <p>Ask questions, get treatment advice, and clarify concerns in real-time.</p>
                        </div>
                        <div className="usp-item">
                            <span className="usp-icon">🔬</span>
                            <h3>Expert Knowledge</h3>
                            <p>Cuts through conflicting online advice with authoritative, science-based diagnoses.</p>
                        </div>
                    </div>
                </section>

                <section id="description" className="landing-section">
                    <h2>The #1 Tool for Saltwater Fish Disease Identification</h2>
                    <p>
                    As a reef keeper, your fish probably mean a lot to you. It's a well known fact that aquarium fish often get sick with potentially fatal diseases like <strong>Ich (Cryptocaryon irritans)</strong>, <strong>Marine Velvet (Amyloodinium ocellatum)</strong>, or <strong>Brooklynella</strong> that can quickly throw your tank into crisis mode. That's why you need Fish MD.
                    </p>
                    <p>
                    Fish MD is a conversational AI system that enables <strong>early diagnosis of health issues in saltwater aquarium fish</strong>. Whether you're seeing white spots on your fish, rapid breathing, scratching behavior, or any other concerning symptoms, Fish MD excels at saltwater fish disease identification and will help you get an accurate and quick diagnosis.
                    </p>
                    <p>
                    Fish MD is easy to use. All you need to do is <strong>upload one or more clear images</strong> of your sick fish with some brief context on their identities and habitat. Fish MD is <strong>free and comprehensive</strong>, allowing for quick diagnosis of common aquarium diseases in situations where time is everything.
                    </p>
                </section>

                <section id="common-diseases" className="landing-section">
                    <h2>Diseases We Can Help Diagnose</h2>
                    <p>Fish MD can identify and help treat over 20 common saltwater fish ailments including:</p>
                    <ul className="disease-list">
                        <li><strong>Marine Ich (White Spot Disease)</strong> - Cryptocaryon irritans</li>
                        <li><strong>Marine Velvet</strong> - Amyloodinium ocellatum</li>
                        <li><strong>Brooklynella (Clownfish Disease)</strong> - Brooklynella hostilis</li>
                        <li><strong>Uronema</strong> - Fast-moving ciliate parasite</li>
                        <li><strong>Flukes</strong> - Gill and body flukes</li>
                        <li><strong>Bacterial Infections</strong> - Fin rot, ulcers, and more</li>
                        <li><strong>HLLE</strong> - Head and Lateral Line Erosion</li>
                        <li><strong>Lymphocystis</strong> - Viral cauliflower-like growths</li>
                        <li>And many more parasitic, bacterial, fungal, and environmental conditions</li>
                    </ul>
                </section>

                <section id="explanation" className="landing-section">
                    <h2>How It Works</h2>
                    <p>
                        1. <strong>Sign In:</strong> Securely log in with your Google account to save your analysis and diagnosis history.
                    </p>
                    <p>
                        2. <strong>Upload Images:</strong> Take clear photos of your sick saltwater aquarium fish showing any visible symptoms like white spots, lesions, or abnormal behavior.
                    </p>
                    <p>
                        3. <strong>Get Instant Diagnosis:</strong> Our AI analyzes the visual symptoms and provides an accurate diagnosis within seconds.
                    </p>
                    <p>
                        4. <strong>Ask Follow-up Questions:</strong> Interact with Fish MD to understand treatment options, medication dosages, and quarantine procedures. Get personalized advice for your specific situation.
                    </p>
                </section>

                <section id="symptoms-preview" className="landing-section">
                    <h2>Common Symptoms We Analyze</h2>
                    <p>Not sure if your fish needs help? Fish MD can identify these warning signs:</p>
                    <div className="symptoms-grid">
                        <div className="symptom-item">White spots (salt-like or dust-like)</div>
                        <div className="symptom-item">Fish rubbing on rocks (flashing)</div>
                        <div className="symptom-item">Rapid or labored breathing</div>
                        <div className="symptom-item">Cloudy or swollen eyes</div>
                        <div className="symptom-item">Frayed or rotting fins</div>
                        <div className="symptom-item">Loss of appetite</div>
                        <div className="symptom-item">Lethargy or hiding behavior</div>
                        <div className="symptom-item">Skin lesions or ulcers</div>
                    </div>
                    <p className="symptoms-cta">See any of these signs? Don't wait - early diagnosis saves lives!</p>
                </section>

                <section id="call-to-action" className="landing-section">
                    <h2>Get Started - Your Fish Need Help Now</h2>
                    <p>
                        Every hour matters when dealing with fish diseases. Don't rely on conflicting forum advice or wait for responses. Get an <strong>instant, accurate diagnosis</strong> from Fish MD's AI-powered visual analyzer.
                    </p>
                    <Link to="/chat-interface" className="cta-button">
                        Diagnose Your Fish Now →
                    </Link>
                    <p className="cta-subtext">Free • No Credit Card Required • Results in Seconds</p>
                </section>

                <footer className="landing-footer">
                    <p>&copy; 2025 Fish MD | <Link to="/symptom-checker">Symptom Checker</Link> | <Link to="/articles">Fish Health Articles</Link> | <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></p>
                </footer>
            </div>
        </>
    );
}

export default LandingPage; 